import { useOcppCommandResults } from '@infuse/cpms/charging-station/queries';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

export interface OcppCommandStatusHandler {
  responseStatus: string;
  notificationType: UiNotificationType;
  translationKey: string;
}

export interface WaitForOcppResultsOptions {
  maxRetries?: number;
  retryInterval?: number;
  timeoutTranslationKey?: string;
  statusHandlers?: OcppCommandStatusHandler[];
}

export class NoOcppCommandResultsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NoOcppCommandResultsError';
  }
}

export const useOcppCommandMutationUtils = () => {
  const {
    data: chargingStationCommandResults,
    apiCorrelationToken,
    refetch,
    isRefetching,
    isLoading: loadingOcppCommands
  } = useOcppCommandResults();

  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });
  const isWaitingForResults = ref(false);

  const waitForOcppResults = async (options: WaitForOcppResultsOptions = {}): Promise<void> => {
    const {
      maxRetries = 10,
      retryInterval = 1000,
      timeoutTranslationKey = 'chargingStation.action.timeout',
      statusHandlers = []
    } = options;

    let attempts = 0;
    isWaitingForResults.value = true;

    while (attempts < maxRetries) {
      await refetch();

      // Wait for refetch to complete
      while (isRefetching.value || loadingOcppCommands.value) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (!chargingStationCommandResults.value) {
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, retryInterval));
        continue;
      }

      const incompleteResults = chargingStationCommandResults.value.results.filter(
        (result) => !result.commandResult || !result.resultTime
      );

      if (incompleteResults.length === 0) {
        // All results are complete
        isWaitingForResults.value = false;

        // Handle status-specific notifications
        for (const handler of statusHandlers) {
          const hasStatus = chargingStationCommandResults.value.results.some(
            (result) =>
              result.commandResult.responseStatus === handler.responseStatus ||
              // Handle set configuration changes
              result.commandResult.componentVariables?.some(
                (cv: { status: string }) => cv.status === handler.responseStatus
              )
          );

          if (hasStatus) {
            notify({
              type: handler.notificationType,
              group: 'notify-top',
              title: t(`${handler.translationKey}.title`),
              message: t(`${handler.translationKey}.message`),
              timeout: 5000
            });
          }
        }

        return;
      }

      attempts++;
      await new Promise((resolve) => setTimeout(resolve, retryInterval));
    }

    // Max retries reached - notify about timeout
    isWaitingForResults.value = false;
    notify({
      type: UiNotificationType.ERROR,
      group: 'notify-top',
      title: t(`${timeoutTranslationKey}.title`),
      message: t(`${timeoutTranslationKey}.message`),
      timeout: 5000
    });

    throw new NoOcppCommandResultsError(
      `OCPP command timeout for API correlation token ${apiCorrelationToken.value}: Results not received within expected time`
    );
  };

  return {
    chargingStationCommandResults,
    waitForOcppResults,
    isWaitingForResults,
    apiCorrelationToken
  };
};
